import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  FuelButton,
  FuelCheckbox,
  FuelTypography,
  FuelDropdown,
  FuelFilter,
} from '@frete.com/fuel-react';
import { FuelBottomSheet, FuelFlex } from '@frete.com/fuel-station-react';
import AppContext from '@context';
import { StatusFilterType } from '@context/reducers/filters/filters.type';
import useAmplitude from '@hooks/useAmplitude';
import useDeviceDetect from '@hooks/useDeviceDetect';
import { FiltersHighlightPropsInterface } from './FiltersOptionsTypes';

function FiltersOptions({ data }: FiltersHighlightPropsInterface) {
  const {
    state: {
      filters: { status: filtersStatusState },
    },
    dispatch,
  } = useContext(AppContext);
  const [filtersDropdownSelectedTemp, setFiltersDropdownSelectedTemp] =
    useState<StatusFilterType[]>([]);
  const [filtersSelectedLabelTemp, setFiltersSelectedLabelTemp] = useState<
    string[]
  >([]);

  const { isMobile } = useDeviceDetect();
  const elementFuelDropdownRef = useRef<HTMLFuelDropdownElement>(null);
  const { amplitudeEvent } = useAmplitude();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const slugsDropDown = data.flatMap((itemDropdown) => itemDropdown.slugs);

  const updateFilters = (
    selectedFilters: StatusFilterType[],
    selectedLabels: string[],
  ) => {
    setFiltersDropdownSelectedTemp(selectedFilters);
    setFiltersSelectedLabelTemp(selectedLabels);
  };

  const clearFiltersDropdown = () => {
    updateFilters([], []);
    const remainingFilters = filtersStatusState.filter(
      (status) => !slugsDropDown.includes(status),
    );
    dispatch({
      type: 'filters_status_update',
      payload: { status: remainingFilters },
    });
    elementFuelDropdownRef.current?.close();
  };

  const handleConfirmSelection = () => {
    amplitudeEvent('journey_filters_button_clicked', { source: 'confirmar' });
    if (!filtersDropdownSelectedTemp.length) {
      clearFiltersDropdown();
      return;
    }
    if (isMobile) setIsOpen(false);
    const updatedFilters = [
      ...filtersStatusState.filter((status) => !slugsDropDown.includes(status)),
      ...filtersDropdownSelectedTemp,
    ];
    dispatch({
      type: 'filters_status_update',
      payload: { status: updatedFilters },
    });
    elementFuelDropdownRef.current?.close();
  };

  useEffect(() => {
    const syncedFilters = filtersStatusState.filter((status) =>
      slugsDropDown.includes(status),
    );
    const syncedFiltersLabels = data
      .filter((item) =>
        filtersStatusState.some((slug) => item.slugs.includes(slug)),
      )
      .map((item) => item.label);
    updateFilters(syncedFilters, syncedFiltersLabels);
  }, [filtersStatusState]);

  const CheckboxOptionsComponent = (
    <FuelFlex
      defaultProps={{
        customMargin: '0 8px 16px',
        flexDirection: 'column',
        gap: 'km6',
      }}
      slot="content"
    >
      {data.map((filterItem, index) => (
        <FuelCheckbox
          key={filterItem.label}
          data-testid={`fuel-checkbox-${index}`}
          label={filterItem.label}
          onFuel-change={() => {
            const isSelected = filtersDropdownSelectedTemp.some((item) =>
              filterItem.slugs.includes(item),
            );
            const updatedFilters = isSelected
              ? filtersDropdownSelectedTemp.filter(
                  (item) => !filterItem.slugs.includes(item),
                )
              : [...filtersDropdownSelectedTemp, ...filterItem.slugs];
            const updatedLabels = isSelected
              ? filtersSelectedLabelTemp.filter(
                  (label) => label !== filterItem.label,
                )
              : [...filtersSelectedLabelTemp, filterItem.label];
            updateFilters(updatedFilters, updatedLabels);
            amplitudeEvent('journey_filters_button_clicked', {
              source: filterItem.label,
            });
          }}
          checked={filtersDropdownSelectedTemp.some((item) =>
            filterItem.slugs.includes(item),
          )}
        />
      ))}
    </FuelFlex>
  );

  return (
    <div>
      {isMobile ? (
        <FuelFilter
          variant="large"
          showIcon
          showDropdownIcon
          badgeContent={filtersSelectedLabelTemp.length || ''}
          actived={!!filtersSelectedLabelTemp.length}
          onClick={() => setIsOpen(true)}
        >
          Status
          <FuelBottomSheet
            title="Filtrar por status"
            confirmText="Confirmar"
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            refuseText="Limpar seleção"
            onConfirm={handleConfirmSelection}
            onRefuse={clearFiltersDropdown}
            portalName="journey-bottomSheet-cancel-journey"
            height={410}
            zIndex={1001}
          >
            {[CheckboxOptionsComponent]}
          </FuelBottomSheet>
        </FuelFilter>
      ) : (
        <FuelFilter
          variant="large"
          showIcon
          showDropdownIcon
          badgeContent={filtersSelectedLabelTemp.length || ''}
          actived={!!filtersSelectedLabelTemp.length}
        >
          Filtro
          <FuelDropdown
            id="fuel-dropdown-filter"
            slot="dropdown"
            ref={elementFuelDropdownRef}
            data-testid="filters-dropdown-desktop"
          >
            <FuelTypography
              slot="header"
              tag="h1"
              size="body-lg"
              color="neutral-strong"
              weight="bold"
            >
              Filtrar por status
            </FuelTypography>
            {[CheckboxOptionsComponent]}
            <FuelButton
              variant="tertiary"
              slot="button"
              onClick={() => {
                clearFiltersDropdown();
                amplitudeEvent('journey_filters_button_clicked', {
                  source: 'Limpar seleção',
                });
              }}
            >
              Limpar seleção
            </FuelButton>
            <FuelButton
              slot="button"
              variant="primary"
              onClick={handleConfirmSelection}
            >
              Confirmar
            </FuelButton>
          </FuelDropdown>
        </FuelFilter>
      )}
    </div>
  );
}

export default FiltersOptions;
